/**
 * Le trajet des notes, mesuré sur l'horloge du service.
 *
 * # Ce qu'on mesure
 *
 * Chaque trame de gestes porte l'instant de son émission sur l'horloge du
 * service — celle contre laquelle chaque poste s'aligne à la jointure, puis
 * toutes les quinze secondes (voir `clock-sync.ts`). Le récepteur lit la même
 * horloge à l'arrivée et fait la différence. C'est un trajet : émission,
 * réseau, réception, et rien d'autre.
 *
 * Rien de la grille n'y entre : ni la position de séance, ni la vue locale, ni
 * la pause, ni le tempo, ni le délai de restitution. Une première version
 * comparait la position de séance à celle que portait le tick de la note.
 * C'est la même différence sur le papier, et chaque chose qui touche la
 * position la cassait : un défilement de la vue pendant qu'on joue a envoyé un
 * « trajet » de dix secondes au service, le 2026-09-13, et le service en a
 * fait le délai de toute la salle. Deux lectures d'une même horloge n'ont rien
 * de tel à casser.
 *
 * L'instant d'émission est celui du **geste** quand la source le connaît —
 * l'horodatage MIDI, voir `gestureInstant` — et non celui de l'envoi :
 * l'attente du fil principal entre les deux est réelle, et le tampon doit la
 * couvrir.
 *
 * # Pourquoi la jambe ne suffisait pas
 *
 * Le service dimensionnait l'attente à partir de la jambe annoncée : l'aller-
 * retour vers le SFU, lu dans `getStats()`. Deux filtres la rendent inapte à
 * ça, et ils se composent.
 *
 * `currentRoundTripTime` n'est rafraîchi qu'au rythme des vérifications de
 * consentement STUN — quelques secondes entre deux valeurs. Une excursion qui
 * dure deux cents millisecondes n'a aucune chance d'être échantillonnée ; c'est
 * du repliement de spectre. Et par-dessus, le client annonce le **minimum**
 * d'une fenêtre de relevés, ce qui décrit bien une liaison et dimensionne mal
 * une attente. Et surtout, un paquet STUN part du poste qui dort : sa radio
 * est réveillée quand la réponse revient. Une note, elle, arrive sur un poste
 * qui n'a rien demandé.
 *
 * Mesuré sur une liaison Wi-Fi réelle : la jambe annonçait 4 ms pendant qu'un
 * `ping` sur le même chemin donnait `min 5 / moy 22 / max 188`. Une note sur
 * cinq sonnait hors de la grille, et rien dans ce que le service lisait ne
 * pouvait le lui dire.
 *
 * Le trajet a un échantillon **par note**, donc au rythme du jeu : aucun
 * repliement possible, et les excursions sont vues parce qu'elles frappent
 * les notes. Il décrit le chemin réellement emprunté par les notes, pas le
 * chemin ICE, qui n'est pas le même objet.
 *
 * # Pourquoi un module et non une valeur passée
 *
 * La mesure naît dans `LiveSession`, à la réception de chaque trame, et se lit
 * au même endroit pour l'annonce — mais le contexte de séance doit pouvoir
 * l'oublier au démontage, et les tests la lire. Même forme que le compteur de
 * dépassement, pour la même raison.
 */

/**
 * Âge au-delà duquel un relevé ne décrit plus la liaison, en millisecondes.
 *
 * Une liaison change — on s'éloigne de la borne, quelqu'un lance un
 * téléchargement. Sans péremption, une rafale de mauvais relevés tiendrait le
 * délai en haut pour le reste de la séance, bien après que la cause a disparu.
 *
 * Trente secondes : assez long pour qu'une accalmie de quelques secondes ne
 * fasse pas oublier un défaut réel, assez court pour qu'une séance retrouve un
 * délai juste après avoir traversé une zone difficile.
 */
const SAMPLE_TTL_MS = 30_000;

/**
 * Nombre de relevés tenus. Au-delà, le plus ancien sort.
 *
 * Quelques notes par seconde en jeu soutenu : deux cents relevés couvrent bien
 * la fenêtre de péremption sans jamais la déborder de beaucoup. La borne n'est
 * là que pour que la mémoire reste constante si quelqu'un joue très vite.
 */
const MAX_SAMPLES = 200;

/**
 * Le rang retenu, entre 0 et 1.
 *
 * # Ce qui se paie des deux côtés
 *
 * Couvrir le maximum absolu ferait payer à toute la salle, durablement, la pire
 * excursion jamais vue : une seule note à 190 ms imposerait 190 ms à tout le
 * monde, pour toujours. Descendre trop bas laisse sonner hors de la grille une
 * part audible du jeu.
 *
 * Mais les deux erreurs ne coûtent pas la même chose, et c'est ce qui tranche.
 * Une note manquée sonne **au plus tôt**, donc en retard de ce qui dépasse le
 * délai — le plus souvent quelques millisecondes, puisque les trajets qui
 * dépassent un haut percentile le dépassent de peu. Un délai trop grand, lui,
 * retarde *toutes* les notes de *tout le monde*, tout le temps.
 *
 * # D'où 0,90 plutôt que 0,95
 *
 * Sur une liaison réelle mesurée en séance — médiane 10 ms, p90 vers 35, p95
 * vers 90, maximum 190 —, les deux rangs donnent des séances très différentes :
 * p95 impose une centaine de millisecondes à tout le monde pour sauver une note
 * sur vingt, p90 en impose une quarantaine et en manque une sur dix, presque
 * toutes de quelques millisecondes seulement.
 *
 * Le réglage se juge à l'oreille et se vérifie à l'écran : le compteur
 * `tampon dépassé` du panneau de séance compte exactement les notes que ce
 * rang laisse passer. C'est la raison pour laquelle ce nombre est ici, nommé,
 * plutôt qu'écrit dans une expression.
 */
const PERCENTILE = 0.9;

/**
 * Au-delà, ce n'est plus un trajet : c'est un écart d'horloges.
 *
 * Une note « partie » une seconde plus tôt n'a pas voyagé une seconde — l'un
 * des deux postes lit mal l'horloge du service. La compter ferait du
 * percentile, donc du délai de toute la salle, la mesure d'un défaut qui n'a
 * rien à voir avec le réseau. Même borne que le plafond du service : ce qu'il
 * ne saurait couvrir n'a pas à être mesuré.
 */
const MAX_TRANSIT_MS = 1000;

/** L'horodatage tient sur 32 bits : les millisecondes du service, modulo ça. */
const STAMP_MODULO = 2 ** 32;

/**
 * L'horodatage d'une trame : l'horloge du service en millisecondes, réduite
 * à 32 bits. Zéro est réservé et veut dire « inconnu » — un émetteur qui
 * n'est pas encore aligné n'a pas d'horloge du service à donner, et une
 * lecture qui tomberait pile sur zéro glisse d'une milliseconde plutôt que de
 * passer pour inconnue.
 */
export function stampOf(serverMs: number | null): number {
  if (serverMs === null || !Number.isFinite(serverMs)) {
    return 0;
  }

  const stamp = Math.round(serverMs) >>> 0;

  return stamp === 0 ? 1 : stamp;
}

/**
 * Le temps écoulé depuis un horodatage, lu sur la même horloge, en
 * millisecondes. `null` quand il n'y a rien à mesurer : horodatage inconnu,
 * ou trame « arrivée avant d'être partie » — ce qui ne dit rien du trajet et
 * tout d'un écart d'horloges, et tirerait le percentile du mauvais côté.
 */
export function elapsedSinceStamp(stamp: number, serverNowMs: number): number | null {
  if (stamp === 0 || !Number.isFinite(serverNowMs)) {
    return null;
  }

  let elapsed = (Math.round(serverNowMs) >>> 0) - (stamp % STAMP_MODULO);

  if (elapsed < 0) {
    elapsed += STAMP_MODULO;
  }

  // Au-delà de la moitié du tour, c'est un négatif qui a fait le tour.
  if (elapsed > STAMP_MODULO / 2) {
    return null;
  }

  return elapsed;
}

interface Sample {
  /** Trajet, en millisecondes. */
  transitMs: number;
  /** `performance.now()` au moment du relevé. */
  at: number;
}

let samples: Sample[] = [];

/**
 * Note le trajet d'un geste reçu, en millisecondes.
 *
 * Un par geste, y compris ceux arrivés trop tard pour leur tick — et c'est le
 * cas qui compte le plus : un trajet plus long que le délai est précisément
 * ce qu'on veut voir remonter.
 */
export function recordTransit(transitMs: number): void {
  if (!Number.isFinite(transitMs) || transitMs < 0 || transitMs > MAX_TRANSIT_MS) {
    return;
  }

  samples.push({ transitMs, at: performance.now() });

  if (samples.length > MAX_SAMPLES) {
    samples.shift();
  }
}

/**
 * Le trajet qu'il faut couvrir, d'après ce qu'on a vu passer : le percentile
 * des relevés encore frais, en millisecondes.
 *
 * Rend `null` tant qu'aucune trame n'a été reçue — au démarrage, chez un
 * leader, dans une salle où personne ne joue. C'est bien `null` et non zéro :
 * zéro dirait « rien à attendre », alors qu'on ne sait pas encore, et le
 * service doit pouvoir retomber sur son estimation à partir des jambes.
 */
export function transitP90(nowMs: number = performance.now()): number | null {
  const deadline = nowMs - SAMPLE_TTL_MS;
  samples = samples.filter((sample) => sample.at >= deadline);

  if (samples.length === 0) {
    return null;
  }

  const sorted = samples.map((sample) => sample.transitMs).sort((a, b) => a - b);
  // Le rang le plus haut dont la proportion reste sous le percentile : avec un
  // seul relevé c'est lui, ce qui est le comportement voulu — un tampon dépassé
  // dès la première note doit se voir sans attendre d'en avoir vingt.
  const index = Math.min(sorted.length - 1, Math.floor(PERCENTILE * sorted.length));

  return sorted[index];
}

/** Oublie tout. Une nouvelle séance ne décrit pas la même liaison. */
export function resetTransit(): void {
  samples = [];
}
